// **React Imports
import { Fragment, memo, useCallback, useMemo, useState } from 'react'

// **MUI Imports
import { ExpandMore, Workspaces } from '@mui/icons-material'
import { Box, Menu, MenuItem, Typography, styled } from '@mui/material'

// **API Imports
import { updateWorkspaceChange } from '../../../axios/api/workspace'

// **Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedWorkspace } from '../../../store/slice/workspace'

// ** Component Imports
import OverlayLoading from '../../overlay-loading'

// **Utils Imports
import { SORT_TYPE, SUBSCRIPTION_PLANS } from '../../../utils/constant'
import { commonClientSort, showErrorToast } from '../../../utils/utils'

// **SCSS Imports
import './index.scss'

// **Styled Component
const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&.selected': {
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.primary.main,

    '&:hover': {
      backgroundColor: theme.palette.primary.main
    },
    '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
      color: `${theme.palette.common.white} !important`
    },
    p: { color: `${theme.palette.common.white} !important` }
  },
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.text.primary
  }
}))

// **static style

const styles = {
  px: 4,
  py: 1.75,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  '& svg': {
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
}

const SwitchWorkspace = (props) => {
  // props
  const { drawerExpanded, currentlyActivePlan } = props
  //Hooks
  const dispatch = useDispatch()

  //Redux State
  const workspaces = useSelector((state) => state.workspace.workspaces)
  const selectedWorkspace = useSelector(
    (state) => state.workspace.selectedWorkspace
  )

  //State
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(false)

  // Variable
  const isEnterprisePlan = useMemo(
    () => currentlyActivePlan?.planName === SUBSCRIPTION_PLANS?.ENTERPRISE,
    [currentlyActivePlan]
  )

  const workspaceObj = useMemo(() => {
    return workspaces?.find((w) => w?.id === selectedWorkspace?.id) || null
  }, [selectedWorkspace, workspaces])

  const sortedWorkspaces = useMemo(() => {
    return workspaces?.length > 0
      ? [...workspaces]?.sort((a, b) =>
          commonClientSort(a?.name, b?.name, 'asc', SORT_TYPE?.STRING)
        )
      : []
  }, [workspaces])

  const getCurrentPath = useCallback(() => {
    const { pathname, search, origin } = window.location
    const path = pathname.split('/')
    let updatedPath = ''
    if (path[1] === 'settings') {
      updatedPath = pathname
    } else if (path[1] === 'subscription') {
      updatedPath = pathname + search
    } else {
      updatedPath = `/${path[1]}`
    }
    return `${origin}${updatedPath}`
  }, [])

  //handlers
  const handleOpen = useCallback((e) => {
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback((e) => {
    setTimeout(() => {
      setAnchorEl(null)
    })
  }, [])

  const handleSwitchWorkspace = useCallback(
    async (workspace) => {
      if (workspaceObj?.id !== workspace?.id) {
        setLoading(true)
        const response = await updateWorkspaceChange(workspace?.id)
        setLoading(false)

        if (response?.isError) {
          showErrorToast(response)
        } else {
          dispatch(setSelectedWorkspace(workspace?.id))
          window.location.href = getCurrentPath()
        }
      } else {
        handleClose()
      }
    },
    [dispatch, handleClose, workspaceObj, getCurrentPath]
  )

  return isEnterprisePlan && workspaces?.length > 0 ? (
    <Fragment>
      {loading && <OverlayLoading />}
      <Box className="workspace-container" onClick={handleOpen}>
        <Workspaces className="icons" />
        <Typography
          className="ellipsis-text"
          {...(workspaceObj?.name ? { title: workspaceObj?.name } : {})}
          sx={drawerExpanded ? { opacity: 1 } : { opacity: 0 }}
        >
          {workspaceObj?.name || 'Select Workspace'}
        </Typography>
        <ExpandMore
          sx={drawerExpanded ? { opacity: 1 } : { opacity: 0 }}
          className="icons"
        />
      </Box>
      <Menu
        disableAutoFocusItem
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose()}
        sx={{
          '& .MuiMenu-paper': {
            minWidth: '230px',
            mt: 1.8
          }
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
      >
        {sortedWorkspaces?.map((workspace) => (
          <MenuItemStyled
            className={`${workspaceObj?.id === workspace.id ? 'selected' : ''}`}
            key={workspace.id}
            sx={{ p: 0 }}
            onClick={() => handleSwitchWorkspace(workspace)}
          >
            <Box sx={styles}>
              <Typography className="ellipsis-text" title={workspace.name}>
                {workspace.name}
              </Typography>
            </Box>
          </MenuItemStyled>
        ))}
      </Menu>
    </Fragment>
  ) : (
    <Fragment />
  )
}

export default memo(SwitchWorkspace)
